import axios from 'axios';
import { AUTH_BASE_URL, AuthUser } from './authApi';

/* ================================================================
   Carrier + SubCarrier dashboard data.

   Carrier quotes come from our API (GET /api/Carrier/quotes), which proxies
   NowCerts PolicyDetailList using the NCToken we forward. SubCarriers never receive a usable NowCerts token — their quotes
   come from our own API (GET /api/SubCarrier/my-quotes), which calls NowCerts
   server side and returns the same OData shape. Both go through
   mapNowCertsQuote so the two dashboards show a quote identically.
   ================================================================ */

export interface Quote {
  id: string;
  /** NowCerts databaseId GUID — the key the SubCarrier assignment API uses.
   *  `id` is the display number (QT-2026-…), which that API does not accept. */
  databaseId?: string;
  /** NowCerts insured GUID — what the Carrier Quote Details page sends to /ai/quote-context. */
  insuredDatabaseId?: string;
  insuredName: string;
  carrierId: number | null;
  carrierName: string;
  status: 'Pending' | 'Approved' | 'Bound' | 'In Review' | 'Declined' | 'Cancelled';
  createdDate: string;
  premium?: number;
  assignedToUserId?: string;
}

export interface CarrierUser {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  isActive: boolean;
  createdDate: string;
}

export interface StatTile {
  label: string;
  value: number;
  tone: 'primary' | 'success' | 'warning' | 'info' | 'danger';
}

export interface Result<T> {
  data: T;
  isSample: boolean;
}

const TIMEOUT = 8000;
// A quotes page is up to 100 rows, which the backend assembles from several NowCerts requests.
const QUOTES_TIMEOUT = 30000;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}` };
}

/* Carrier quotes come from our backend, which proxies NowCerts. The session's
   NCToken is passed along in X-NowCerts-Token. */
const BACKEND_CARRIER_URL = `${AUTH_BASE_URL}/api/Carrier`;
const BACKEND_QUOTES_URL = `${BACKEND_CARRIER_URL}/quotes`;

interface NowCertsPolicyRow {
  databaseId?: string;
  insuredDatabaseId?: string | null;
  number?: string | null;
  insuredCommercialName?: string | null;
  insuredFirstName?: string | null;
  insuredLastName?: string | null;
  carrierName?: string | null;
  status?: string | null;
  bindDate?: string | null;
  createDate?: string | null;
  totalPremium?: number | null;
}

interface NowCertsPage {
  value?: NowCertsPolicyRow[];
  '@odata.count'?: number;
}

/** The backend may return the OData envelope ({ value: [...] }), a bare array,
 *  or wrap the rows in data/items/quotes/result. Accept any of them. */
function rowsOf(body: any): NowCertsPolicyRow[] {
  if (Array.isArray(body)) return body;
  for (const key of ['value', 'data', 'items', 'quotes', 'result', 'results']) {
    const v = body?.[key];
    if (Array.isArray(v)) return v;
    if (Array.isArray(v?.value)) return v.value;
  }
  return [];
}

/** "2026-10-01T08:52:00-05:00" -> "10/01/2026". Read off the string so the
 *  browser's timezone can't move the date. */
export function formatNowCertsDate(value?: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
}

/** Commercial name, otherwise "Last, First". */
function insuredNameOf(row: NowCertsPolicyRow): string {
  const commercial = row.insuredCommercialName?.trim();
  if (commercial) return commercial;
  const person = [row.insuredLastName?.trim(), row.insuredFirstName?.trim()].filter(Boolean).join(', ');
  return person || 'Unknown insured';
}

/** Bound once it has a bind date, otherwise Cancelled or Pending. */
export function quoteStatusOf(row: Pick<NowCertsPolicyRow, 'bindDate' | 'status'>): Quote['status'] {
  return row.bindDate ? 'Bound' : row.status === 'Cancelled' ? 'Cancelled' : 'Pending';
}

function mapNowCertsQuote(row: NowCertsPolicyRow): Quote {
  const status = quoteStatusOf(row);
  return {
    id: row.number || row.databaseId || '',
    databaseId: row.databaseId,
    insuredDatabaseId: row.insuredDatabaseId || undefined,
    insuredName: insuredNameOf(row),
    carrierId: null,
    carrierName: row.carrierName || '',
    status,
    createdDate: formatNowCertsDate(row.createDate),
    premium: row.totalPremium ?? undefined,
  };
}

/** Most rows the backend returns per request — the largest page size the table offers. */
export const QUOTES_BLOCK_SIZE = 100;

function quotesHeaders() {
  const ncToken = localStorage.getItem('NCToken') || '';
  // igtoken authenticates us to our backend; NCToken (when the session has one) is for the backend to use against NowCerts.
  return {
    ...authHeaders(),
    Accept: 'application/json',
    ...(ncToken ? { 'X-NowCerts-Token': ncToken } : {}),
  };
}

export interface QuotesPage {
  rows: Quote[];
  /** Quotes matching the search across all pages, not just this one. */
  total: number;
}

/**
 * One window of the carrier's quotes, newest first: up to `top` (max 100) rows from
 * offset `skip`, optionally narrowed by a search on quote number / insured name.
 * Throws on failure.
 */
export async function fetchCarrierQuotesPage(skip: number, top: number, search = ''): Promise<QuotesPage> {
  const params: Record<string, string | number> = { skip, top };
  if (search.trim()) params.search = search.trim();

  const body: NowCertsPage = (await axios.get(BACKEND_QUOTES_URL, {
    headers: quotesHeaders(),
    params,
    timeout: QUOTES_TIMEOUT,
  })).data;

  const rows = rowsOf(body).map(mapNowCertsQuote);
  return { rows, total: body?.['@odata.count'] ?? skip + rows.length };
}

export interface QuotesSummary {
  total: number;
  bound: number;
  cancelled: number;
  pending: number;
}

/** Counts over all of the carrier's quotes, for the dashboard cards. Throws on failure. */
export async function fetchCarrierQuotesSummary(): Promise<QuotesSummary> {
  const res = await axios.get(`${BACKEND_QUOTES_URL}/summary`, {
    headers: quotesHeaders(),
    timeout: QUOTES_TIMEOUT,
  });
  return res.data;
}

function newestFirst(rows: NowCertsPolicyRow[]): NowCertsPolicyRow[] {
  const created = (r: NowCertsPolicyRow) => Date.parse(r.createDate ?? '') || 0;
  return [...rows].sort((a, b) => created(b) - created(a));
}

/**
 * The signed-in SubCarrier's assigned quotes, newest first.
 *
 * Our API calls NowCerts server side — the browser never touches NowCerts or an
 * NCToken for this. The whole list comes back at once; sorting and paging are ours.
 * Throws SubCarrierApiError carrying the HTTP status (401 expired, 403 not a
 * SubCarrier, 502 NowCerts unreachable, 500 other).
 */
export function fetchMyQuotes(): Promise<Quote[]> {
  // Callers that overlap (StrictMode's double mount, a quick Retry) share one
  // request. It clears when it settles, so the next visit still fetches fresh.
  if (!myQuotesInFlight) {
    myQuotesInFlight = requestMyQuotes().finally(() => { myQuotesInFlight = null; });
  }
  return myQuotesInFlight;
}

let myQuotesInFlight: Promise<Quote[]> | null = null;

async function requestMyQuotes(): Promise<Quote[]> {
  try {
    const res = await axios.get(`${AUTH_BASE_URL}/api/SubCarrier/my-quotes`, {
      headers: authHeaders(),
      timeout: TIMEOUT,
    });
    const rows = rowsOf(res.data);
    return newestFirst(rows).map(mapNowCertsQuote);
  } catch (err) {
    throw toApiError(err, 'Could not load your quotes.');
  }
}

export async function fetchCarrierUsers(): Promise<Result<CarrierUser[]>> {
  const res = await axios.get(`${AUTH_BASE_URL}/api/SubCarrier`, {
    headers: authHeaders(),
    timeout: TIMEOUT,
  });
  const rows = Array.isArray(res.data) ? res.data : [];
  return { data: rows.map(mapSubCarrier), isSample: false };
}

function mapSubCarrier(row: any): CarrierUser {
  const firstName = String(row?.firstName ?? '');
  const lastName = String(row?.lastName ?? '');
  return {
    id: String(row?.userId ?? ''),
    firstName,
    lastName,
    fullName: [firstName, lastName].filter(Boolean).join(' ') || String(row?.email ?? ''),
    email: String(row?.email ?? ''),
    phoneNumber: String(row?.phoneNumber ?? ''),
    isActive: row?.activeUser !== false,
    createdDate: row?.created ? new Date(row.created).toLocaleDateString('en-US') : '',
  };
}

export function carrierCount(quotes: Quote[]): number {
  return new Set(quotes.map(q => q.carrierId).filter(v => v !== null)).size;
}

/** Fields POST /api/SubCarrier/register accepts. */
export interface CarrierUserInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber: string;
}

/** UserType enum shared with the API — 3 is a SubCarrier. */
export const USER_TYPE_SUBCARRIER = 3;

export class RegisterError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'RegisterError';
  }
}

/**
 * Register a sub-carrier under the signed-in carrier admin.
 *
 * parentUserId must equal the caller's UserId claim — the API rejects any
 * mismatch with a 403, so a carrier can only create users under itself.
 */
export async function createCarrierUser(
  parentUserId: number,
  input: CarrierUserInput
): Promise<CarrierUser> {
  try {
    const res = await axios.post(
      `${AUTH_BASE_URL}/api/SubCarrier/register`,
      {
        email: input.email.trim(),
        password: input.password,
        firstName: input.firstName.trim(),
        lastName: input.lastName.trim(),
        phoneNumber: input.phoneNumber.trim(),
        parentUserId,
        userType: USER_TYPE_SUBCARRIER,
      },
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' }, timeout: TIMEOUT }
    );

    return {
      id: String(res.data?.userId ?? ''),
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      fullName: [input.firstName, input.lastName].filter(Boolean).join(' ').trim(),
      email: res.data?.username || input.email.trim(),
      phoneNumber: input.phoneNumber.trim(),
      isActive: true,
      createdDate: new Date().toLocaleDateString('en-US'),
    };
  } catch (err: any) {
    const status = err?.response?.status;
    const apiMessage = err?.response?.data?.message || err?.response?.data?.Message;

    if (status === 409) throw new RegisterError('A user with this email already exists.', status);
    if (status === 403) throw new RegisterError('You can only add users under your own carrier account.', status);
    if (status === 401) throw new RegisterError('Your session has expired. Sign in again.', status);
    if (status === 400) throw new RegisterError(apiMessage || 'Check the details and try again.', status);
    if (!status) throw new RegisterError("Can't reach the server. Check the API is running.", status);
    throw new RegisterError(apiMessage || 'Could not add the user. Please try again.', status);
  }
}

export async function updateCarrierUser(
  userId: string,
  input: CarrierUserInput
): Promise<void> {
  try {
    await axios.put(
      `${AUTH_BASE_URL}/api/SubCarrier/${userId}`,
      {
        email: input.email.trim(),
        firstName: input.firstName.trim(),
        lastName: input.lastName.trim() || null,
        phoneNumber: input.phoneNumber.trim() || null,
        // Omitted entirely when blank — the API keeps the current password.
        ...(input.password ? { password: input.password } : {}),
      },
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' }, timeout: TIMEOUT }
    );
  } catch (err: any) {
    const status = err?.response?.status;
    const apiMessage = err?.response?.data?.message || err?.response?.data?.Message;

    if (status === 409) throw new RegisterError('A user with this email already exists.', status);
    if (status === 404) throw new RegisterError('That user no longer exists.', status);
    if (status === 401) throw new RegisterError('Your session has expired. Sign in again.', status);
    if (status === 400) throw new RegisterError(apiMessage || 'Check the details and try again.', status);
    if (!status) throw new RegisterError("Can't reach the server. Check the API is running.", status);
    throw new RegisterError(apiMessage || 'Could not save the changes. Please try again.', status);
  }
}

/** A failed SubCarrier API call, carrying the HTTP status so the UI can
 *  tell an expired session (401) from a stale selection (404). */
export class SubCarrierApiError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'SubCarrierApiError';
  }
}

function toApiError(err: any, fallback: string): SubCarrierApiError {
  const status: number | undefined = err?.response?.status;
  const apiMessage = err?.response?.data?.message || err?.response?.data?.Message;
  if (!status) return new SubCarrierApiError("Can't reach the server. Check the API is running.");
  return new SubCarrierApiError(apiMessage || fallback, status);
}

export interface AssignedSubCarrier {
  id: string;
  fullName: string;
}

/** SubCarriers currently assigned to a quote. Always fresh — never cached. */
export async function fetchQuoteAssignments(quoteId: string): Promise<AssignedSubCarrier[]> {
  try {
    const res = await axios.get(
      `${AUTH_BASE_URL}/api/SubCarrier/quotes/${encodeURIComponent(quoteId)}`,
      { headers: authHeaders(), timeout: TIMEOUT }
    );
    const rows: any[] = Array.isArray(res.data) ? res.data : [];
    return rows
      .filter(r => r?.userId !== undefined && r?.userId !== null)
      .map(r => ({
        id: String(r.userId),
        fullName: [r.firstName, r.lastName].filter(Boolean).join(' ').trim() || String(r.email ?? ''),
      }));
  } catch (err) {
    throw toApiError(err, 'Could not load the current assignments.');
  }
}

/** Replace the quote's assignments with exactly these userIds ([] unassigns everyone). */
export async function assignQuoteSubCarriers(quoteId: string, userIds: number[]): Promise<void> {
  try {
    await axios.post(
      `${AUTH_BASE_URL}/api/SubCarrier/quotes/${encodeURIComponent(quoteId)}/assign`,
      { subCarrierUserIds: userIds },
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' }, timeout: TIMEOUT }
    );
  } catch (err) {
    throw toApiError(err, 'Could not save the assignment. Please try again.');
  }
}

export function countByStatus(quotes: Quote[], status: Quote['status']): number {
  return quotes.filter(q => q.status === status).length;
}

export function scopeLabel(user: AuthUser | null): string {
  if (!user) return '';
  if (!user.isCarrierUser) return 'All carriers';
  return user.carrierName || 'Your carrier';
}
